import {Box, Lock, LockStatus, Occupancy} from "@variocube/app-api";
import {
    Avatar,
    Badge, classNames,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    layout,
    MaterialIcon,
    PushButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    typography,
    useAsync,
    View,
    ViewActions,
    ViewContent,
    ViewHeader
} from "@variocube/cube-ui";
import * as React from "react";
import {Fragment, useCallback, useEffect, useState} from "react";
import {AccessLevel, Controller} from "../controller/Controller";
import {naturalCompare} from "@variocube/app-common";
import {useLocalization} from "../../i18n";
import {BoxDetailView} from "./BoxDetailView";
import {BoxProperties} from "./BoxProperties";
import {LockState} from "./LockState";

interface BoxListViewProps {
    controller: Controller;
    accessLevel: AccessLevel;
    secondary: boolean;
}

export function BoxListView({controller, accessLevel, secondary}: BoxListViewProps) {
    const {t} = useLocalization();
    const canOpenAll = accessLevel == AccessLevel.FullAccess;
    const canOpenNonOccupied = accessLevel == AccessLevel.FullAccess || accessLevel == AccessLevel.OpenNonOccupied;

    const [boxes, setBoxes] = useState<Box[]>([]);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [occupancies, setOccupancies] = useState<Occupancy[]>([]);
    const [locks, setLocks] = useState<Lock[]>([]);
    const {result, execute: fetchData} = useAsync(() => Promise.all([
        controller.getBoxes(),
        controller.getOccupancies(),
        controller.getLocks(),
    ]), [controller]);
    const [boxDetails, setBoxDetails] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (result) {
            const [boxes, occupancies, locks] = result;
            setBoxes(boxes.sort((a, b) => naturalCompare(a.number, b.number)));
            setOccupancies(occupancies);
            setLocks(locks);
        }
    }, [result]);

    const handleLockStatus = useCallback((lock: string, status: LockStatus) => {
        setLocks(locks => locks.map(l => l.handle == lock ? {...l, status} : l));
    }, []);

    useEffect(() => {
        controller.addLockStatusListener(handleLockStatus);
        return () => controller.removeLockStatusListener(handleLockStatus);
    }, [controller, handleLockStatus]);

    const openBoxes = useCallback(async (boxes: Box[]) => {
        const locks = boxes
            .map(box => secondary ? box.secondaryLock : box.lock)
            .filter(lock => !!lock) as string[];
        for (const lock of locks) {
            try {
                await controller.open(lock);
            }
            catch (error) {
                console.error("Error while opening lock", error);
            }
        }
    }, [controller, secondary]);

    const getLock = useCallback((box: Box) => {
        return locks.find(lock => lock.handle == (secondary ? box.secondaryLock : box.lock))
    }, [locks, secondary]);

    const getOccupancy = useCallback((box: Box) => {
        return occupancies.find(occupancy => occupancy.boxNumber == box.number);
    }, [occupancies]);

    const getAllOccupancies = useCallback( (box: Box) => {
        return occupancies.filter(o => o.boxNumber === box.number);
    },[occupancies]);

    const handleOpenAllClick = useCallback(async () => {
        await openBoxes(boxes);
    }, [openBoxes, boxes]);

    const handleOpenNonOccupiedClick = useCallback(async () => {
        await openBoxes(boxes.filter(box => !isOccupied(box, occupancies) && !isMaster(box)));
    }, [openBoxes, boxes, occupancies]);

    const handleEndAllOccupancies = useCallback(  async () => {
        const allOccupancies = await controller.getOccupancies();
        for(const occupancy of allOccupancies){
            try{
                await controller.endOccupancy(occupancy.uuid);
            }catch(e){
                console.error("Failed to end occupancy", e);
            }
        }
        fetchData();
        setShowDialog(false);
    }, [controller])

    const handleCancel = useCallback( () => {
       setShowDialog(false);
    }, [setShowDialog]);

    const canOpen = useCallback((box: Box) => {
        switch (accessLevel) {
            case AccessLevel.FullAccess: return true;
            case AccessLevel.OpenNonOccupied: return !isOccupied(box, occupancies) && !isMaster(box);
            default: return false;
        }
    }, [accessLevel, occupancies]);

    const handleOpen = useCallback((box: Box) => openBoxes([box]), [openBoxes]);

    const handleRowClick = useCallback( (boxIndex: number) => {
        setBoxDetails(boxIndex);
    }, [boxDetails]);

    const handleBack = useCallback( () => {
        setBoxDetails(undefined);
        fetchData();
    }, []);

    if(boxDetails != undefined) {
        return(
            <BoxDetailView
              box={boxes[boxDetails]}
              occupancies={getAllOccupancies(boxes[boxDetails])}
              canOpen={canOpen(boxes[boxDetails])}
              onOpen={handleOpen}
              accessLevel={accessLevel}
              controller={controller}
              onBack={handleBack}
            />
        );
    }

    return (
        <View>
            <ViewHeader>
                <ViewActions>
                    {canOpenAll && (
                        <PushButton
                            icon={MaterialIcon.lock_open}
                            className={layout.marginSmall}
                            onClick={handleOpenAllClick}
                        >
                            {t("OpenAll")}
                        </PushButton>
                    )}
                    {canOpenNonOccupied && (
                        <PushButton
                            icon={MaterialIcon.lock_open}
                            className={layout.marginSmall}
                            onClick={handleOpenNonOccupiedClick}
                        >
                            {t("OpenNonOccupied")}
                        </PushButton>
                    )}
                    {canOpenAll && (
                        <PushButton
                            icon={MaterialIcon.lock_open}
                            className={layout.marginSmall}
                            onClick={() => setShowDialog(true)}
                        >
                            {t("EndAllOccupancies")}
                        </PushButton>
                    )}
                    <PushButton
                        icon={MaterialIcon.refresh}
                        className={layout.marginSmall}
                        onClick={fetchData}
                    >
                        {t("Refresh")}
                    </PushButton>
                </ViewActions>
            </ViewHeader>
            <ViewContent>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("Box")}</TableCell>
                            <TableCell>{t("Types")}</TableCell>
                            <TableCell>{t("Status")}</TableCell>
                            <TableCell>{t("Enabled")}</TableCell>
                            <TableCell>{t("Occupied")}</TableCell>
                            <TableCell>{t("Maintenance")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {boxes.map((box, index) => (
                            <BoxTableRow
                                key={box.number}
                                box={box}
                                lock={getLock(box)}
                                occupancy={getOccupancy(box)}
                                canOpen={canOpen(box)}
                                onOpen={handleOpen}
                                onRowClick={() => handleRowClick(index)}
                            />
                        ))}
                    </TableBody>
                </Table>
            </ViewContent>
            <Dialog
              open={showDialog}
            >
                <DialogHeader title={t("EndAllOccupancies")} />
                <DialogContent>{t("EndAllOccupanciesMessage")}</DialogContent>
                <DialogFooter>
                    <PushButton primary onClick={handleEndAllOccupancies}>{t("EndAllOccupancies")}</PushButton>
                    {' '}
                    <PushButton primary onClick={handleCancel}>{t("Cancel")}</PushButton>
                </DialogFooter>
            </Dialog>
        </View>
    )
}

interface BoxTableRowProps {
    box: Box;
    lock?: Lock;
    occupancy?: Occupancy;
    canOpen: boolean;
    onOpen: (box: Box) => void;
    onRowClick: () => void;
}

function BoxTableRow({box, lock, occupancy, canOpen, onOpen, onRowClick}: BoxTableRowProps) {
    const {t} = useLocalization();

    const handleOpen = useCallback(() => onOpen(box), [onOpen, box]);

    return (
        <TableRow onClick={() => onRowClick()}>
            <TableCell>
                <Avatar>{box.number}</Avatar>
            </TableCell>
            <TableCell>
                <BoxProperties box={box} />
            </TableCell>
            <TableCell>
                <LockState lock={lock} />
            </TableCell>
            <TableCell>
                <div className={classNames(typography.bigger, typography.alignCenter)}>{box.disabled ? MaterialIcon.do_not_disturb_on : MaterialIcon.check}</div>
            </TableCell>
            <TableCell>
                {occupancy && (
                    <Fragment>
                        <Badge>{t("Occupied")}</Badge>
                        <br/>
                        {occupancy.appId}
                    </Fragment>
                )}
            </TableCell>
            <TableCell>
                {canOpen && (
                    <PushButton
                        icon={MaterialIcon.lock_open}
                        primary
                        onClick={handleOpen}
                    >
                        {t("Open")}
                    </PushButton>
                )}
                {box.maintenanceRequiredAt && (
                    <span>{box.maintenanceRequiredAt}</span>
                )}
            </TableCell>
        </TableRow>
    )
}

function isOccupied(box: Box, occupancies: Occupancy[]) {
    return occupancies.filter(occupancy => occupancy.boxNumber == box.number).length > 0;
}

function isMaster(box: Box) {
    return box.types.indexOf("Master") >= 0;
}